/**
 * ReplayPanel.tsx — MER Algo Footprints v7.1B — Phase 7.1B
 *
 * Read-only sequential replay of Journal71A entries.
 *
 * Each snapshot card shows:
 *   - symbol · setup · direction · score · confidence · regime · gamma status
 *   - operator action + outcome/note
 *   - ranked competitor table: full scanner order at log time (from rankedSnapshot)
 *     showing relative position, alternatives, and how the selected setup compared
 *
 * HISTORICAL DATA — All replay cards show captured snapshots from the time of
 * the original log action. Nothing is recomputed. Read-only.
 *
 * Never mutates live state. Never triggers recomputation.
 * All data is read-only from journal71aEntries in the Zustand store.
 */

import React, { useMemo, useState } from 'react';
import { useStore } from '../../store';
import { Journal71AEntry, Journal71AActionType } from '../../types';
import { exportReplaySnapshotJSON } from './export71b';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
    + ` ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

const ACTION_COLOR: Record<Journal71AActionType, string> = {
  trade_taken:      'var(--green)',
  trade_skipped:    'var(--amber)',
  setup_viewed:     'var(--blue-dim)',
  note_added:       'var(--cyan)',
  outcome_reviewed: 'var(--purple)',
};

const ACTION_LABEL: Record<Journal71AActionType, string> = {
  trade_taken:      'TAKEN',
  trade_skipped:    'SKIPPED',
  setup_viewed:     'VIEWED',
  note_added:       'NOTE ADDED',
  outcome_reviewed: 'OUTCOME REVIEWED',
};

const OUTCOME_COLOR = {
  win: 'var(--green)', loss: 'var(--red)', breakeven: 'var(--amber)',
} as const;

function scoreColor(v: number | null): string {
  if (v === null) return 'var(--t3)';
  return v >= 70 ? 'var(--green)' : v >= 45 ? 'var(--amber)' : 'var(--red)';
}

function dirColor(d: string | null): string {
  return d === 'long' ? 'var(--green)' : d === 'short' ? 'var(--red)' : 'var(--blue-dim)';
}

// ─── Ranked competitor table ──────────────────────────────────────────────────
// Shows the full scanner order captured at log time.
// The row for the entry's symbol is highlighted.

type SnapshotRow = NonNullable<Journal71AEntry['rankedSnapshot']>[number];

function RankedSnapshotView({ snapshot, highlightSymbol }: {
  snapshot: SnapshotRow[];
  highlightSymbol: string;
}) {
  if (!snapshot.length) return null;

  const highlightRank = snapshot.find(r => r.symbol === highlightSymbol)?.rank ?? null;

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{
        fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
        color: 'var(--blue-dim)', letterSpacing: '1px', textTransform: 'uppercase',
        marginBottom: 5,
      }}>
        Scanner order at log time
        {highlightRank !== null && (
          <span style={{ color: 'var(--gold)', marginLeft: 6, fontWeight: 400 }}>
            · selected was #{highlightRank} of {snapshot.length}
          </span>
        )}
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 3, overflow: 'hidden' }}>
        {snapshot.map((row, i) => {
          const isSelected = row.symbol === highlightSymbol;
          const dc = row.direction === 'long' ? 'var(--green)'
            : row.direction === 'short' ? 'var(--red)' : 'var(--amber)';
          const sc = scoreColor(row.score);
          const barW = `${Math.round(row.score)}%`;

          return (
            <div key={`${row.symbol}-${i}`} style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '4px 8px',
              background: isSelected ? 'var(--gold)10' : i % 2 === 0 ? 'var(--surface)' : 'transparent',
              borderLeft: isSelected ? '2px solid var(--gold)' : '2px solid transparent',
              borderBottom: i < snapshot.length - 1 ? '1px solid var(--surface)' : 'none',
            }}>
              {/* Rank */}
              <span style={{
                fontSize: 8, fontFamily: 'var(--mono)',
                color: isSelected ? 'var(--gold)' : 'var(--t3)',
                minWidth: 14, textAlign: 'right',
              }}>#{row.rank}</span>

              {/* Symbol */}
              <span style={{
                fontSize: 10, fontFamily: 'var(--mono)', fontWeight: isSelected ? 700 : 400,
                color: isSelected ? 'var(--gold)' : 'var(--text)',
                minWidth: 40,
              }}>{row.symbol}</span>

              {/* Setup type */}
              <span style={{
                fontSize: 7, fontFamily: 'var(--mono)',
                color: dc, border: `1px solid ${dc}44`, background: `${dc}10`,
                padding: '0 4px', borderRadius: 2, flexShrink: 0,
              }}>{row.type.replace(/_/g,' ')}</span>

              {/* Score bar */}
              <div style={{ flex: 1, height: 3, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: barW, height: '100%', background: sc, borderRadius: 2 }} />
              </div>

              {/* Score number */}
              <span style={{
                fontSize: 10, fontFamily: 'var(--mono)', fontWeight: 700,
                color: sc, minWidth: 22, textAlign: 'right',
              }}>{row.score}</span>

              {/* Direction badge */}
              <span style={{ fontSize: 8, fontFamily: 'var(--mono)', color: dc, minWidth: 28 }}>
                {row.direction}
              </span>

              {/* Selected marker */}
              {isSelected && (
                <span style={{ fontSize: 7, color: 'var(--gold)', fontFamily: 'var(--mono)', fontWeight: 700 }}>
                  ← SELECTED
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Snapshot card ────────────────────────────────────────────────────────────

function SnapshotCard({ entry, index, total }: {
  entry: Journal71AEntry; index: number; total: number;
}) {
  const actionColor = ACTION_COLOR[entry.actionType];

  return (
    <div style={{
      background: 'var(--surface2)', border: '1px solid var(--border)',
      borderLeft: `3px solid ${actionColor}`,
      borderRadius: '0 6px 6px 0', padding: '14px 16px',
    }}>

      {/* Trust header — counter + timestamp + read-only badge + export */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 8, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '1px' }}>
          ENTRY {index + 1} / {total} · {fmt(entry.ts)}
        </span>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <span style={{
            fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700,
            color: 'var(--blue-dim)', border: '1px solid var(--border)',
            background: 'var(--surface)', padding: '1px 5px', borderRadius: 2,
            letterSpacing: '.5px',
          }}>HISTORICAL SNAPSHOT · READ-ONLY</span>
          <button
            onClick={() => exportReplaySnapshotJSON(entry)}
            title="Export this snapshot as JSON"
            style={{
              fontSize: 7, fontFamily: 'var(--mono)', fontWeight: 700,
              color: 'var(--blue-dim)', border: '1px solid var(--border)',
              background: 'none', padding: '1px 5px', borderRadius: 2, cursor: 'pointer',
            }}>⬇ JSON</button>
        </div>
      </div>

      {/* Symbol + action badge + outcome */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--gold)', fontFamily: 'var(--mono)' }}>
          {entry.symbol}
        </span>
        <span style={{
          fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)',
          color: actionColor, border: `1px solid ${actionColor}55`, background: `${actionColor}15`,
          padding: '2px 8px', borderRadius: 3,
        }}>{ACTION_LABEL[entry.actionType]}</span>
        {entry.outcome && (
          <span style={{
            fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)',
            color: OUTCOME_COLOR[entry.outcome],
            border: `1px solid ${OUTCOME_COLOR[entry.outcome]}55`,
            background: `${OUTCOME_COLOR[entry.outcome]}15`,
            padding: '2px 8px', borderRadius: 3, textTransform: 'uppercase',
          }}>{entry.outcome}</span>
        )}
      </div>

      {/* Setup snapshot grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 20px', marginBottom: 10 }}>
        {[
          { l: 'Setup',      v: entry.setupType?.replace(/_/g,' ') ?? '—', c: dirColor(entry.direction) },
          { l: 'Direction',  v: entry.direction ?? '—',                     c: dirColor(entry.direction) },
          { l: 'Score',      v: entry.score !== null ? String(entry.score) : '—', c: scoreColor(entry.score) },
          { l: 'Confidence', v: entry.confidence !== null ? String(entry.confidence) : '—', c: 'var(--purple)' },
          { l: 'Regime',     v: entry.regime ?? '—',                         c: 'var(--gold)' },
          { l: 'Gamma',      v: entry.gammaStatus ?? '—',                    c: 'var(--blue-dim)' },
        ].map(({ l, v, c }) => (
          <div key={l} style={{ display: 'flex', gap: 6 }}>
            <span style={{ fontSize: 9, color: 'var(--t3)', minWidth: 70, fontFamily: 'var(--sans)' }}>{l}</span>
            <span style={{ fontSize: 9, color: c, fontFamily: 'var(--mono)', fontWeight: 600 }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Note */}
      {entry.note && (
        <div style={{
          fontSize: 10, color: 'var(--text)', background: 'var(--surface)',
          border: '1px solid var(--border)', borderRadius: 3,
          padding: '6px 9px', lineHeight: 1.5, marginBottom: 4,
          fontStyle: 'italic',
        }}>
          "{entry.note}"
        </div>
      )}

      {/* Ranked competitor table — Phase 7.1B completion */}
      {entry.rankedSnapshot && entry.rankedSnapshot.length > 0 ? (
        <RankedSnapshotView snapshot={entry.rankedSnapshot} highlightSymbol={entry.symbol} />
      ) : (
        <div style={{
          marginTop: 8, fontSize: 8, color: 'var(--t3)', fontStyle: 'italic',
          borderTop: '1px solid var(--surface)', paddingTop: 6,
        }}>
          No scanner snapshot — logged before v7.1B snapshot capture
        </div>
      )}
    </div>
  );
}

// ─── Filter bar ───────────────────────────────────────────────────────────────

function FilterBar({ symFilter, setSymFilter, actionFilter, setActionFilter, symbols }: {
  symFilter: string; setSymFilter: (v: string) => void;
  actionFilter: Journal71AActionType | 'all'; setActionFilter: (v: Journal71AActionType | 'all') => void;
  symbols: string[];
}) {
  const sel: React.CSSProperties = {
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 3, color: 'var(--text)', fontFamily: 'var(--sans)',
    fontSize: 11, padding: '4px 7px', outline: 'none',
  };

  const actionLabel: Record<string, string> = {
    all: 'All actions', trade_taken: 'Taken', trade_skipped: 'Skipped',
    setup_viewed: 'Viewed', note_added: 'Note', outcome_reviewed: 'Outcome',
  };

  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
      <select value={symFilter} onChange={e => setSymFilter(e.target.value)} style={{ ...sel, minWidth: 90 }}>
        <option value="all">All symbols</option>
        {symbols.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <select value={actionFilter} onChange={e => setActionFilter(e.target.value as any)} style={{ ...sel, minWidth: 110 }}>
        {(Object.keys(actionLabel) as (Journal71AActionType | 'all')[]).map(a => (
          <option key={a} value={a}>{actionLabel[a]}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function ReplayPanel() {
  const entries = useStore(s => s.journal71aEntries);

  const [symFilter,    setSymFilter]    = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<Journal71AActionType | 'all'>('all');
  const [cursor,       setCursor]       = useState(0);

  const symbols = useMemo(
    () => [...new Set(entries.map(e => e.symbol))].sort(),
    [entries],
  );

  const filtered = useMemo(() => {
    let r = [...entries].sort((a, b) => b.ts - a.ts);
    if (symFilter !== 'all')    r = r.filter(e => e.symbol === symFilter);
    if (actionFilter !== 'all') r = r.filter(e => e.actionType === actionFilter);
    return r;
  }, [entries, symFilter, actionFilter]);

  React.useEffect(() => { setCursor(0); }, [symFilter, actionFilter]);

  if (!entries.length) {
    return (
      <div style={{ padding: '28px 0', color: 'var(--t3)', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t2)', marginBottom: 8 }}>
          ⏮ Replay Mode
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.7 }}>
          No entries to replay yet.<br />
          <span style={{ color: 'var(--blue-dim)' }}>
            In the Dashboard → expand a scanner row → ▼ ATTRIBUTION<br />
            → click VIEWED, TAKEN, or SKIPPED to log your first entry.
          </span>
        </div>
      </div>
    );
  }

  const current = filtered[cursor] ?? null;

  const navBtn = (disabled: boolean): React.CSSProperties => ({
    fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 700,
    color: disabled ? 'var(--t3)' : 'var(--text)',
    border: `1px solid ${disabled ? 'var(--surface)' : 'var(--border)'}`,
    background: 'none', padding: '4px 14px', borderRadius: 3,
    cursor: disabled ? 'default' : 'pointer',
  });

  return (
    <div>
      <FilterBar
        symFilter={symFilter} setSymFilter={setSymFilter}
        actionFilter={actionFilter} setActionFilter={setActionFilter}
        symbols={symbols}
      />

      {filtered.length === 0 ? (
        <div style={{ fontSize: 10, color: 'var(--t3)', padding: '12px 0' }}>
          No entries match the current filter.
        </div>
      ) : (
        <>
          {current && <SnapshotCard entry={current} index={cursor} total={filtered.length} />}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, justifyContent: 'center' }}>
            <button
              disabled={cursor === 0}
              onClick={() => setCursor(p => Math.max(0, p - 1))}
              style={navBtn(cursor === 0)}>
              ◀ NEWER
            </button>
            <span style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', minWidth: 60, textAlign: 'center' }}>
              {cursor + 1} / {filtered.length}
            </span>
            <button
              disabled={cursor >= filtered.length - 1}
              onClick={() => setCursor(p => Math.min(filtered.length - 1, p + 1))}
              style={navBtn(cursor >= filtered.length - 1)}>
              OLDER ▶
            </button>
          </div>
        </>
      )}
    </div>
  );
}
